import {notifyError, reportError} from '../../utils/notify';
import { formatNumberDisplay, formatNumberInput, parseNumberInput } from '../../utils/numberFormat';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  createProduct,
  deleteProductImageById,
  fetchNextProductCode,
  fetchProduct,
  fetchProductImageByIdUrl,
  setPrimaryProductImage,
  updateProduct,
  uploadProductImages,
} from '../../api/product';
import {
  PRODUCT_CATEGORIES,
  PRODUCT_UNITS,
  Product,
  ProductImageMeta,
  ProductPayload,
} from '../../types/product';
import ProductTabPlaceholder from './ProductTabPlaceholder';
import ProductPartsTab from './ProductPartsTab';
import ProductCertificatesTab from './ProductCertificatesTab';
import InventoryStockHistory from './InventoryStockHistory';
import ProductInspectionsTab from './ProductInspectionsTab';
import ProductTabBar from './ProductTabBar';
import ProductTagChips from './ProductTagChips';
import { ProductTabKey } from './productTabConfig';
import '../contract/ContractForm.css';
import './ProductForm.css';

const MAX_PRODUCT_IMAGES = 8;
const EMPTY_FORM: ProductPayload = {
  productCode: '',
  productName: '',
  category: '완제품',
  spec: '',
  barcode: '',
  manufacturer: '',
  unit: 'EA',
  costPrice: null,
  supplyPrice: null,
  stockQty: null,
  safetyStock: null,
  storageLocation: '',
  vatType: 'TAXABLE',
  useYn: 'Y',
  tags: [],
  memo: '',
  stockAdjustReason: '',
};

type ExistingGalleryItem = {
  kind: 'existing';
  key: string;
  imageId: number;
  fileName?: string | null;
  url: string;
};

type PendingGalleryItem = {
  kind: 'pending';
  key: string;
  file: File;
  url: string;
};

type GalleryItem = ExistingGalleryItem | PendingGalleryItem;

interface ProductFormProps {
  mode: 'create' | 'edit';
  productId?: number;
  onCancel: () => void;
  onSaved: (product: Product) => void;
}

function ProductForm({ mode, productId, onCancel, onSaved }: ProductFormProps) {
  const isEdit = mode === 'edit';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<ProductPayload>(EMPTY_FORM);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const [deletedImageIds, setDeletedImageIds] = useState<number[]>([]);
  const [primaryKey, setPrimaryKey] = useState<string | null>(null);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ProductTabKey>('general');
  const [productCodeHint, setProductCodeHint] = useState('');
  const [linkedInventoryId, setLinkedInventoryId] = useState<number | null>(null);
  const [originalStockQty, setOriginalStockQty] = useState<number | null>(null);

  useEffect(() => {
    setActiveTab('general');
    if (!isEdit || productId == null) {
      setForm({ ...EMPTY_FORM });
      setLinkedInventoryId(null);
      setOriginalStockQty(null);
      setGallery((prev) => {
        prev.forEach((item) => window.URL.revokeObjectURL(item.url));
        return [];
      });
      setDeletedImageIds([]);
      setPrimaryKey(null);
      setPreviewKey(null);
      setProductCodeHint('');
      setLoading(true);
      let cancelled = false;
      const loadSuggestion = async () => {
        try {
          const suggestion = await fetchNextProductCode();
          if (cancelled) return;
          const last = suggestion.lastValue?.trim() || '';
          const suggested = suggestion.suggestedValue?.trim() || '';
          setForm((prev) => ({ ...prev, productCode: suggested }));
          setProductCodeHint(
            last
              ? `직전 제품코드: ${last} → 끝자리 증가값을 제안했습니다. 필요하면 수정하세요.`
              : '이 회사의 첫 제품코드입니다. 규칙을 정해 입력하세요.'
          );
        } catch {
          if (!cancelled) {
            setProductCodeHint('최근 제품코드를 불러오지 못했습니다. 직접 입력하세요.');
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      };
      void loadSuggestion();
      return () => {
        cancelled = true;
      };
    }
    let cancelled = false;
    const objectUrls: string[] = [];
    const load = async () => {
      try {
        setLoading(true);
        setProductCodeHint('');
        const product = await fetchProduct(productId);
        if (cancelled) return;
        setForm({
          productCode: product.productCode ?? '',
          productName: product.productName,
          category: product.category || '완제품',
          spec: product.spec ?? '',
          barcode: product.barcode ?? '',
          manufacturer: product.manufacturer ?? '',
          unit: product.unit || 'EA',
          costPrice: product.costPrice ?? null,
          supplyPrice: product.supplyPrice ?? null,
          stockQty: product.stockQty ?? null,
          safetyStock: product.safetyStock ?? null,
          storageLocation: product.storageLocation ?? '',
          vatType: product.vatType === 'TAX_FREE' ? 'TAX_FREE' : 'TAXABLE',
          useYn: product.useYn === 'N' ? 'N' : 'Y',
          tags: product.tags ?? [],
          memo: product.memo ?? '',
          stockAdjustReason: '',
        });
        setLinkedInventoryId(product.inventoryId ?? null);
        setOriginalStockQty(product.stockQty ?? 0);
        const metas = product.images ?? [];
        const items: GalleryItem[] = [];
        for (const meta of metas) {
          try {
            const url = await fetchProductImageByIdUrl(productId, meta.imageId);
            objectUrls.push(url);
            items.push({
              kind: 'existing',
              key: `existing-${meta.imageId}`,
              imageId: meta.imageId,
              fileName: meta.fileName,
              url,
            });
          } catch {
            // skip broken
          }
        }
        if (cancelled) {
          objectUrls.forEach((url) => window.URL.revokeObjectURL(url));
          return;
        }
        setGallery((prev) => {
          prev.forEach((item) => window.URL.revokeObjectURL(item.url));
          return items;
        });
        setDeletedImageIds([]);
        const primaryMeta = metas.find((m) => m.primary) ?? metas[0];
        const nextPrimary = primaryMeta ? `existing-${primaryMeta.imageId}` : null;
        setPrimaryKey(nextPrimary);
        setPreviewKey(nextPrimary ?? items[0]?.key ?? null);
        setError('');
      } catch {
        reportError(setError, '제품을 불러오지 못했습니다.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
      objectUrls.forEach((url) => window.URL.revokeObjectURL(url));
    };
  }, [isEdit, productId]);

  const previewItem = useMemo(
    () => gallery.find((item) => item.key === previewKey) ?? gallery[0] ?? null,
    [gallery, previewKey]
  );

  const setNumberField = (key: keyof ProductPayload, raw: string) => {
    const formatted = formatNumberInput(raw);
    const parsed = parseNumberInput(formatted);
    setForm((prev) => ({
      ...prev,
      [key]: parsed == null ? null : Math.max(0, parsed),
    }));
  };

  const handleImagePick = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const remaining = MAX_PRODUCT_IMAGES - gallery.length;
    if (remaining <= 0) {
      notifyError(`제품 사진은 최대 ${MAX_PRODUCT_IMAGES}장까지 등록할 수 있습니다.`);
      return;
    }
    const accepted: PendingGalleryItem[] = [];
    for (const file of Array.from(files).slice(0, remaining)) {
      const lower = file.name.toLowerCase();
      const ok = ['.jpg', '.jpeg', '.png', '.webp', '.gif'].some((ext) => lower.endsWith(ext));
      if (!ok) {
        notifyError('이미지는 jpg, jpeg, png, webp, gif만 등록할 수 있습니다.');
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        notifyError('이미지 용량은 10MB 이하여야 합니다.');
        continue;
      }
      const key = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      accepted.push({
        kind: 'pending',
        key,
        file,
        url: window.URL.createObjectURL(file),
      });
    }
    if (accepted.length === 0) return;
    setError('');
    setGallery((prev) => [...prev, ...accepted]);
    setPrimaryKey((current) => current ?? accepted[0]?.key ?? null);
    setPreviewKey((current) => current ?? accepted[0]?.key ?? null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveItem = (key: string) => {
    setGallery((prev) => {
      const target = prev.find((item) => item.key === key);
      if (!target) return prev;
      if (target.kind === 'existing') {
        setDeletedImageIds((ids) => [...ids, target.imageId]);
      }
      window.URL.revokeObjectURL(target.url);
      const next = prev.filter((item) => item.key !== key);
      setPrimaryKey((current) => {
        if (current !== key) return current;
        return next[0]?.key ?? null;
      });
      setPreviewKey((current) => {
        if (current !== key) return current;
        return next[0]?.key ?? null;
      });
      return next;
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const productName = form.productName.trim();
    if (!productName) {
      notifyError('제품명을 입력해 주세요.');
      return;
    }
    const nextStock = toAmount(form.stockQty);
    const category = (form.category || '').trim();
    const needsStockReason =
      (isEdit &&
        (originalStockQty ?? 0) !== nextStock &&
        (linkedInventoryId != null || category === '완제품')) ||
      (!isEdit && nextStock > 0 && category === '완제품');
    if (needsStockReason && !form.stockAdjustReason?.trim()) {
      notifyError(
        isEdit
          ? '현재고를 변경할 때는 사유를 입력해 주세요.'
          : '초기 재고를 등록할 때는 사유를 입력해 주세요.'
      );
      return;
    }
    const payload: ProductPayload = {
      productCode: form.productCode?.trim() || undefined,
      productName,
      category: category || undefined,
      spec: form.spec?.trim() || undefined,
      barcode: form.barcode?.trim() || undefined,
      manufacturer: form.manufacturer?.trim() || undefined,
      unit: form.unit?.trim() || 'EA',
      costPrice: toAmount(form.costPrice),
      supplyPrice: toAmount(form.supplyPrice),
      stockQty: nextStock,
      safetyStock: toAmount(form.safetyStock),
      storageLocation: form.storageLocation?.trim() || undefined,
      vatType: form.vatType === 'TAX_FREE' ? 'TAX_FREE' : 'TAXABLE',
      useYn: form.useYn === 'N' ? 'N' : 'Y',
      tags: (form.tags ?? []).map((tag) => tag.trim()).filter(Boolean),
      memo: form.memo?.trim() || undefined,
      ...(needsStockReason ? { stockAdjustReason: form.stockAdjustReason?.trim() } : {}),
    };
    try {
      setSaving(true);
      setError('');
      let saved =
        isEdit && productId != null
          ? await updateProduct(productId, payload)
          : await createProduct(payload);

      for (const imageId of deletedImageIds) {
        await deleteProductImageById(saved.productId, imageId);
      }
      if (deletedImageIds.length > 0) {
        saved = await fetchProduct(saved.productId);
      }

      const pending = gallery.filter((item): item is PendingGalleryItem => item.kind === 'pending');
      const beforeIds = new Set((saved.images ?? []).map((img) => img.imageId));
      if (pending.length > 0) {
        saved = await uploadProductImages(
          saved.productId,
          pending.map((item) => item.file)
        );
      }

      const afterImages: ProductImageMeta[] = saved.images ?? [];
      const newlyUploaded = afterImages
        .filter((img) => !beforeIds.has(img.imageId))
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      let targetPrimaryId: number | null = null;
      if (primaryKey?.startsWith('existing-')) {
        const id = Number(primaryKey.replace('existing-', ''));
        if (afterImages.some((img) => img.imageId === id)) {
          targetPrimaryId = id;
        }
      } else if (primaryKey?.startsWith('pending-')) {
        const pendingIndex = pending.findIndex((item) => item.key === primaryKey);
        if (pendingIndex >= 0 && newlyUploaded[pendingIndex]) {
          targetPrimaryId = newlyUploaded[pendingIndex].imageId;
        }
      }
      if (targetPrimaryId == null && afterImages.length > 0) {
        targetPrimaryId = afterImages.find((img) => img.primary)?.imageId ?? afterImages[0].imageId;
      }
      if (targetPrimaryId != null) {
        const currentPrimary = afterImages.find((img) => img.primary)?.imageId;
        if (currentPrimary !== targetPrimaryId) {
          saved = await setPrimaryProductImage(saved.productId, targetPrimaryId);
        }
      }

      onSaved(saved);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '제품 저장에 실패했습니다.';
      notifyError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="contract-form-page contract-form-page--embedded">
      <header className="contract-form-page__header">
        <div>
          <p className="contract-form-page__eyebrow">PRODUCT</p>
          <h1>{isEdit ? '제품 수정' : '제품 등록'}</h1>
          <p>사진, 원가/공급단가, 재고 등 제품 정보를 입력하세요.</p>
        </div>
        <div className="contract-form-page__header-actions">
          <button type="button" className="contract-form-page__ghost" onClick={onCancel}>
            목록으로
          </button>
        </div>
      </header>

      <div className="contract-form-page__main">
        {loading ? (
          <p className="contract-form-page__hint">불러오는 중...</p>
        ) : (
          <>
            <ProductTabBar activeTab={activeTab} onChange={setActiveTab} />

            {activeTab === 'general' ? (
              <form className="contract-form-page__form" onSubmit={handleSubmit}>
                <section className="contract-form-page__card">
                  <div className="contract-form-page__card-head">
                    <h2>제품 사진</h2>
                    <span>
                      최대 {MAX_PRODUCT_IMAGES}장 · jpg · png · webp · gif / 장당 10MB
                    </span>
                  </div>
                  <div className="product-form__gallery">
                    <div className="product-form__gallery-main">
                      <div className="product-form__image-preview product-form__image-preview--lg">
                        {previewItem ? (
                          <img src={previewItem.url} alt="제품 미리보기" />
                        ) : (
                          <span>사진 없음</span>
                        )}
                      </div>
                      <div className="product-form__image-actions">
                        <label>
                          사진 추가
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            multiple
                            onChange={(e) => handleImagePick(e.target.files)}
                          />
                        </label>
                        {previewItem && previewItem.key !== primaryKey && (
                          <button
                            type="button"
                            onClick={() => setPrimaryKey(previewItem.key)}
                          >
                            대표로 지정
                          </button>
                        )}
                        {previewItem && (
                          <button
                            type="button"
                            className="is-danger"
                            onClick={() => handleRemoveItem(previewItem.key)}
                          >
                            이 사진 삭제
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="product-form__gallery-side">
                      <div className="product-form__thumbs">
                        {gallery.map((item) => (
                          <button
                            key={item.key}
                            type="button"
                            className={[
                              'product-form__thumb',
                              item.key === previewKey ? 'is-active' : '',
                              item.key === primaryKey ? 'is-primary' : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={() => setPreviewKey(item.key)}
                            title={
                              item.kind === 'existing'
                                ? item.fileName || '제품 사진'
                                : item.file.name
                            }
                          >
                            <img src={item.url} alt="" />
                            {item.key === primaryKey && (
                              <span className="product-form__thumb-badge">대표</span>
                            )}
                          </button>
                        ))}
                      </div>
                      <p className="product-form__hint">
                        {gallery.length}/{MAX_PRODUCT_IMAGES}장 · 썸네일 클릭으로 미리보기, 대표
                        사진은 목록에 표시됩니다.
                      </p>
                    </div>
                  </div>
                </section>

                <section className="contract-form-page__card">
                  <div className="contract-form-page__card-head">
                    <h2>기본 정보</h2>
                    <span>코드 · 명칭 · 분류</span>
                  </div>
                  <div className="contract-form-page__grid">
                    <label>
                      제품코드
                      <input
                        value={form.productCode}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, productCode: e.target.value }))
                        }
                        maxLength={100}
                        placeholder={isEdit ? undefined : '자동 제안값'}
                      />
                    </label>
                    {!isEdit && productCodeHint ? (
                      <p className="product-form__hint" style={{ gridColumn: '1 / -1', margin: 0 }}>
                        {productCodeHint}
                      </p>
                    ) : null}
                    <label>
                      제품명
                      <input
                        value={form.productName}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, productName: e.target.value }))
                        }
                        maxLength={255}
                        required
                      />
                    </label>
                    <label>
                      분류
                      <select
                        value={form.category || '완제품'}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, category: e.target.value }))
                        }
                      >
                        {PRODUCT_CATEGORIES.map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      규격
                      <input
                        value={form.spec}
                        onChange={(e) => setForm((prev) => ({ ...prev, spec: e.target.value }))}
                        maxLength={255}
                        placeholder="예: 100x50mm"
                      />
                    </label>
                    <label>
                      바코드
                      <input
                        value={form.barcode}
                        onChange={(e) => setForm((prev) => ({ ...prev, barcode: e.target.value }))}
                        maxLength={100}
                      />
                    </label>
                    <label>
                      제조사/공급처
                      <input
                        value={form.manufacturer}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, manufacturer: e.target.value }))
                        }
                        maxLength={255}
                      />
                    </label>
                    <label>
                      단위
                      <select
                        value={form.unit || 'EA'}
                        onChange={(e) => setForm((prev) => ({ ...prev, unit: e.target.value }))}
                      >
                        {PRODUCT_UNITS.map((unit) => (
                          <option key={unit} value={unit}>
                            {unit}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      과세구분
                      <select
                        value={form.vatType || 'TAXABLE'}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            vatType: e.target.value === 'TAX_FREE' ? 'TAX_FREE' : 'TAXABLE',
                          }))
                        }
                      >
                        <option value="TAXABLE">과세</option>
                        <option value="TAX_FREE">면세</option>
                      </select>
                    </label>
                    <label>
                      사용여부
                      <select
                        value={form.useYn || 'Y'}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            useYn: e.target.value === 'N' ? 'N' : 'Y',
                          }))
                        }
                      >
                        <option value="Y">사용</option>
                        <option value="N">미사용</option>
                      </select>
                    </label>
                    <div className="is-span-2">
                      <span className="product-form__field-label">해시태그</span>
                      <ProductTagChips
                        tags={form.tags ?? []}
                        editable
                        onChange={(tags) => setForm((prev) => ({ ...prev, tags }))}
                      />
                      <p className="product-form__hint">
                        예: #ks인증 · Enter/쉼표로 추가 · 목록에서도 표시됩니다.
                      </p>
                    </div>
                  </div>
                </section>

                <section className="contract-form-page__card">
                  <div className="contract-form-page__card-head">
                    <h2>가격 · 재고</h2>
                    <span>원가 · 공급단가 · 재고</span>
                  </div>
                  <div className="contract-form-page__grid">
                    <label>
                      원가
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formatNumberDisplay(form.costPrice)}
                        placeholder="0"
                        onChange={(e) => setNumberField('costPrice', e.target.value)}
                      />
                    </label>
                    <label>
                      공급단가
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formatNumberDisplay(form.supplyPrice)}
                        placeholder="0"
                        onChange={(e) => setNumberField('supplyPrice', e.target.value)}
                      />
                    </label>
                    <label>
                      현재고
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formatNumberDisplay(form.stockQty)}
                        placeholder="0"
                        onChange={(e) => setNumberField('stockQty', e.target.value)}
                      />
                    </label>
                    <label>
                      안전재고
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formatNumberDisplay(form.safetyStock)}
                        placeholder="0"
                        onChange={(e) => setNumberField('safetyStock', e.target.value)}
                      />
                    </label>
                    {(isEdit &&
                      (originalStockQty ?? 0) !== toAmount(form.stockQty) &&
                      (linkedInventoryId != null ||
                        (form.category || '').trim() === '완제품')) ||
                    (!isEdit &&
                      toAmount(form.stockQty) > 0 &&
                      (form.category || '').trim() === '완제품') ? (
                      <label className="is-span-2">
                        재고 변경 사유 *
                        <input
                          value={form.stockAdjustReason ?? ''}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, stockAdjustReason: e.target.value }))
                          }
                          maxLength={500}
                          placeholder="예: 실사 조정, 초기 재고 등록"
                        />
                      </label>
                    ) : null}
                    <label>
                      창고위치
                      <input
                        value={form.storageLocation}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, storageLocation: e.target.value }))
                        }
                        maxLength={100}
                        placeholder="예: A-01-03"
                      />
                    </label>
                    <label className="is-span-2 contract-form-page__memo">
                      비고
                      <textarea
                        value={form.memo}
                        onChange={(e) => setForm((prev) => ({ ...prev, memo: e.target.value }))}
                        maxLength={2000}
                        rows={4}
                        placeholder="제품 설명, 스펙, 취급 주의사항 등을 입력하세요."
                      />
                    </label>
                  </div>
                </section>

                {error && <p className="contract-form-page__error">{error}</p>}

                <div className="contract-form-page__actions">
                  <button type="submit" className="contract-form-page__primary" disabled={saving}>
                    {saving ? '저장 중...' : isEdit ? '수정 저장' : '등록'}
                  </button>
                  <button type="button" className="contract-form-page__ghost" onClick={onCancel}>
                    취소
                  </button>
                </div>
              </form>
            ) : activeTab === 'parts' && isEdit && productId != null ? (
              <ProductPartsTab productId={productId} />
            ) : activeTab === 'certificates' && isEdit && productId != null ? (
              <ProductCertificatesTab productId={productId} />
            ) : activeTab === 'inspections' && isEdit && productId != null ? (
              <ProductInspectionsTab
                inventoryId={linkedInventoryId}
                itemKind="FINISHED"
                emptyHint="이 제품에 연동된 재고 품목이 없습니다. 분류가 완제품이고 재고관리와 연결되면 검사이력이 표시됩니다."
              />
            ) : activeTab === 'stockHistory' && isEdit && productId != null ? (
              <InventoryStockHistory
                inventoryId={linkedInventoryId}
                unit={form.unit}
                emptyHint="이 제품에 연동된 재고 품목이 없습니다. 분류가 완제품이고 재고관리와 연결되면 이력이 표시됩니다."
              />
            ) : (
              <ProductTabPlaceholder tab={activeTab} requireSave={!isEdit} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function toAmount(value: number | null | undefined) {
  if (value == null || Number.isNaN(Number(value))) {
    return 0;
  }
  return Math.max(0, Number(value));
}

export default ProductForm;
